import { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { Loader2, Save, KeyRound, AlertCircle, Link2, Unlink, Copy, CheckCircle2, Database, Image, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

function Field({ label, value, onChange, type = "text", placeholder, hint, testid, disabled }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#475569] mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testid}
        className="w-full bg-white border border-[#E2E8F0] rounded-md px-3 py-2 text-sm focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none disabled:bg-[#F8F9FA] disabled:text-[#64748B]"
      />
      {hint && <div className="text-xs text-[#64748B] mt-1">{hint}</div>}
    </div>
  );
}

export default function Settings() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    accurate_client_id: "",
    accurate_client_secret: "",
    accurate_db_host: "",
    pancake_api_key: "",
    pancake_phone_number_id: "",
    pancake_flow_id: "",
    active_whatsapp_provider: "botcake",
    wallo_api_url: "",
    wallo_api_key: "",
    wallo_device_id: "",
    mock_mode: true,
    auto_sync_enabled: false,
    auto_sync_interval: 15,
    auto_send_new_invoice: false,
  });
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [databases, setDatabases] = useState(null);
  const [loadingDbs, setLoadingDbs] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await api.get("/settings");
      if (!alive) return;
      setData(data);
      setForm((f) => ({
        ...f,
        accurate_client_id: data.accurate_client_id || "",
        accurate_db_host: data.accurate_db_host || "",
        pancake_phone_number_id: data.pancake_phone_number_id || "",
        pancake_flow_id: data.pancake_flow_id || "",
        active_whatsapp_provider: data.active_whatsapp_provider || "botcake",
        wallo_api_url: data.wallo_api_url || "https://wallo.id/api/v1",
        wallo_api_key: "",
        wallo_device_id: data.wallo_device_id || "",
        mock_mode: data.mock_mode,
        auto_sync_enabled: data.auto_sync_enabled || false,
        auto_sync_interval: data.auto_sync_interval || 15,
        auto_send_new_invoice: data.auto_send_new_invoice || false,
      }));
      // Load branding previews
      try {
        const [logoRes, faviconRes] = await Promise.all([
          fetch(`${API}/branding/logo/exists`).then(r => r.json()),
          fetch(`${API}/branding/favicon/exists`).then(r => r.json()),
        ]);
        if (logoRes.exists) setLogoPreview(`${API}/branding/logo?t=${Date.now()}`);
        else setLogoPreview(null);
        if (faviconRes.exists) setFaviconPreview(`${API}/branding/favicon?t=${Date.now()}`);
        else setFaviconPreview(null);
      } catch {}
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  const handleBrandingUpload = async (assetType, file) => {
    const setter = assetType === "logo" ? setUploadingLogo : setUploadingFavicon;
    setter(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/branding/${assetType}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${assetType === "logo" ? "Logo" : "Favicon"} berhasil diupload`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Upload gagal: " + (e.response?.data?.detail || e.message));
    } finally {
      setter(false);
    }
  };

  const handleBrandingDelete = async (assetType) => {
    try {
      await api.delete(`/branding/${assetType}`);
      toast.success(`${assetType === "logo" ? "Logo" : "Favicon"} berhasil dihapus`);
      if (assetType === "logo") setLogoPreview(null);
      else setFaviconPreview(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Gagal menghapus: " + (e.response?.data?.detail || e.message));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.accurate_client_secret) delete payload.accurate_client_secret;
      if (!payload.pancake_api_key) delete payload.pancake_api_key;
      if (!payload.wallo_api_key) delete payload.wallo_api_key;
      await api.put("/settings", payload);
      toast.success("Pengaturan disimpan");
      setForm((f) => ({ ...f, accurate_client_secret: "", pancake_api_key: "", wallo_api_key: "" }));
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Gagal: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const connectAccurate = async () => {
    setConnecting(true);
    try {
      // First save current Client ID/Secret if provided
      if (form.accurate_client_id || form.accurate_client_secret) {
        const sp = {};
        if (form.accurate_client_id) sp.accurate_client_id = form.accurate_client_id;
        if (form.accurate_client_secret) sp.accurate_client_secret = form.accurate_client_secret;
        await api.put("/settings", { ...form, ...sp });
      }
      const { data } = await api.get("/oauth/accurate/url");
      window.location.href = data.url;
    } catch (e) {
      toast.error("Gagal: " + (e.response?.data?.detail || e.message));
      setConnecting(false);
    }
  };

  const loadDatabases = async () => {
    setLoadingDbs(true);
    try {
      const { data } = await api.get("/oauth/accurate/databases");
      setDatabases(data);
    } catch (e) {
      toast.error("Gagal ambil database: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingDbs(false);
    }
  };

  const selectDatabase = async (dbItem) => {
    try {
      await api.post("/oauth/accurate/select-db", { db_id: dbItem.id, alias: dbItem.alias });
      toast.success(`Database "${dbItem.alias}" terpilih`);
      setDatabases(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Gagal: " + (e.response?.data?.detail || e.message));
    }
  };

  const disconnect = async () => {
    try {
      await api.post("/oauth/accurate/disconnect");
      toast.success("Accurate dilepas");
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Gagal: " + (e.response?.data?.detail || e.message));
    }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(data.webhook_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Webhook URL disalin");
  };

  if (!data) {
    return (
      <div className="p-10 flex items-center gap-2 text-sm text-[#64748B]">
        <Loader2 className="w-4 h-4 animate-spin" /> Memuat...
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-[#0F172A]">Settings</h1>
        <p className="text-sm text-[#64748B] mt-1">
          Konfigurasi integrasi Accurate.id (OAuth) & Pancake.id (API key + webhook).
        </p>
      </div>

      {/* Branding */}
      <div className="border border-[#E2E8F0] rounded-lg bg-white p-6 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <Image className="w-4 h-4 text-[#0A2540]" />
          <h2 className="text-base font-medium text-[#0F172A]">Branding</h2>
        </div>
        <p className="text-xs text-[#64748B] mb-4">Upload logo dan favicon untuk tampilan kustom di sidebar, halaman login, dan ikon tab browser.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Logo Upload */}
          <div>
            <div className="text-xs font-medium text-[#475569] mb-2">Logo (Sidebar & Login)</div>
            <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-4 flex flex-col items-center justify-center min-h-[120px] bg-[#F8F9FA]">
              {logoPreview ? (
                <div className="flex flex-col items-center gap-2">
                  <img src={logoPreview} alt="Logo" className="max-h-16 max-w-[160px] object-contain rounded" />
                  <div className="flex gap-2 mt-1">
                    <button onClick={() => logoInputRef.current?.click()} className="text-xs text-[#0066FF] hover:underline flex items-center gap-1">
                      <Upload className="w-3 h-3" /> Ganti
                    </button>
                    <button onClick={() => handleBrandingDelete("logo")} className="text-xs text-[#DC2626] hover:underline flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Hapus
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="text-xs text-[#64748B] hover:text-[#0066FF] flex flex-col items-center gap-1 transition-colors"
                >
                  {uploadingLogo ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  <span>{uploadingLogo ? "Mengupload..." : "Upload Logo"}</span>
                  <span className="text-[10px] text-[#94A3B8]">PNG, JPG, SVG (maks 2MB)</span>
                </button>
              )}
            </div>
            <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={(e) => { if (e.target.files[0]) handleBrandingUpload("logo", e.target.files[0]); e.target.value = ""; }} />
          </div>
          {/* Favicon Upload */}
          <div>
            <div className="text-xs font-medium text-[#475569] mb-2">Favicon (Ikon Tab Browser)</div>
            <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-4 flex flex-col items-center justify-center min-h-[120px] bg-[#F8F9FA]">
              {faviconPreview ? (
                <div className="flex flex-col items-center gap-2">
                  <img src={faviconPreview} alt="Favicon" className="w-10 h-10 object-contain rounded" />
                  <div className="flex gap-2 mt-1">
                    <button onClick={() => faviconInputRef.current?.click()} className="text-xs text-[#0066FF] hover:underline flex items-center gap-1">
                      <Upload className="w-3 h-3" /> Ganti
                    </button>
                    <button onClick={() => handleBrandingDelete("favicon")} className="text-xs text-[#DC2626] hover:underline flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Hapus
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => faviconInputRef.current?.click()}
                  disabled={uploadingFavicon}
                  className="text-xs text-[#64748B] hover:text-[#0066FF] flex flex-col items-center gap-1 transition-colors"
                >
                  {uploadingFavicon ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  <span>{uploadingFavicon ? "Mengupload..." : "Upload Favicon"}</span>
                  <span className="text-[10px] text-[#94A3B8]">PNG, ICO (maks 500KB)</span>
                </button>
              )}
            </div>
            <input ref={faviconInputRef} type="file" accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml" className="hidden" onChange={(e) => { if (e.target.files[0]) handleBrandingUpload("favicon", e.target.files[0]); e.target.value = ""; }} />
          </div>
        </div>
      </div>

      <div className="border border-[#E2E8F0] rounded-lg bg-white p-6 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-[#0F172A]">MOCK Mode</div>
            <div className="text-xs text-[#64748B] mt-1">
              Aktifkan untuk uji coba dengan data dummy. Otomatis mati saat Accurate terhubung.
            </div>
          </div>
          <Switch
            checked={form.mock_mode}
            onCheckedChange={(v) => setForm({ ...form, mock_mode: v })}
            data-testid="settings-mock-toggle"
          />
        </div>
      </div>

      {/* Accurate.id */}
      <div className="border border-[#E2E8F0] rounded-lg bg-white p-6 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#0A2540]" />
            <h2 className="text-base font-medium text-[#0F172A]">Accurate.id</h2>
          </div>
          {data.accurate_connected ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#059669] text-xs font-medium">
              <CheckCircle2 className="w-3 h-3" /> Terhubung
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#DC2626] text-xs font-medium">
              Belum terhubung
            </span>
          )}
        </div>

        <div className="space-y-4">
          <Field
            label="Client ID"
            value={form.accurate_client_id}
            onChange={(e) => setForm({ ...form, accurate_client_id: e.target.value })}
            placeholder="42f12a10-08df-4b91-..."
            testid="settings-accurate-client-id"
          />
          <Field
            label="Client Secret"
            type="password"
            value={form.accurate_client_secret}
            onChange={(e) => setForm({ ...form, accurate_client_secret: e.target.value })}
            placeholder={data.accurate_client_secret_masked || "Belum diisi"}
            hint={data.accurate_client_secret_masked ? `Saat ini: ${data.accurate_client_secret_masked}` : null}
            testid="settings-accurate-client-secret"
          />

          {data.accurate_connected && (
            <div className="grid grid-cols-2 gap-4 p-3 bg-[#F8F9FA] rounded-md text-xs">
              <div>
                <div className="text-[#64748B]">Database</div>
                <div className="text-[#0F172A] font-medium mt-0.5">{data.accurate_db_alias || "-"}</div>
              </div>
              <div>
                <div className="text-[#64748B]">Host</div>
                <div className="text-[#0F172A] font-mono mt-0.5 truncate">{data.accurate_db_host || "-"}</div>
              </div>
            </div>
          )}

          <div className="flex gap-2 flex-wrap pt-2">
            {!data.accurate_connected ? (
              <button
                onClick={connectAccurate}
                disabled={connecting || !form.accurate_client_id}
                data-testid="connect-accurate-button"
                className="bg-[#0A2540] text-white hover:bg-[#1E3A8A] rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-60"
              >
                {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Connect Accurate.id
              </button>
            ) : (
              <>
                <button
                  onClick={loadDatabases}
                  disabled={loadingDbs}
                  data-testid="change-database-button"
                  className="bg-white border border-[#E2E8F0] text-[#0F172A] hover:bg-[#F8F9FA] rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2"
                >
                  {loadingDbs ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                  Ganti Database
                </button>
                <button
                  onClick={disconnect}
                  data-testid="disconnect-accurate-button"
                  className="bg-white border border-[#FEE2E2] text-[#DC2626] hover:bg-[#FEE2E2] rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2"
                >
                  <Unlink className="w-4 h-4" />
                  Disconnect
                </button>
              </>
            )}
          </div>

          {databases && (
            <div className="border border-[#E2E8F0] rounded-md divide-y divide-[#E2E8F0]">
              {databases.length === 0 && (
                <div className="px-4 py-3 text-xs text-[#64748B]">Tidak ada database.</div>
              )}
              {databases.map((d) => (
                <button
                  key={d.id}
                  onClick={() => selectDatabase(d)}
                  data-testid={`select-db-${d.id}`}
                  className="w-full px-4 py-3 text-left hover:bg-[#F8F9FA] text-sm flex items-center justify-between transition-colors"
                >
                  <span className="text-[#0F172A] font-medium">{d.alias}</span>
                  <span className="text-xs text-[#64748B] font-mono">ID: {d.id}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp Provider Selector */}
      <div className="border border-[#E2E8F0] rounded-lg bg-white p-6 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-4 h-4 text-[#0A2540]" />
          <h2 className="text-base font-medium text-[#0F172A]">WhatsApp Provider</h2>
        </div>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="provider"
              value="botcake"
              checked={form.active_whatsapp_provider === "botcake"}
              onChange={() => setForm({ ...form, active_whatsapp_provider: "botcake" })}
              className="text-[#0066FF] focus:ring-[#0066FF]"
            />
            <span className="text-sm text-[#0F172A]">Pancake.id (Botcake)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="provider"
              value="wallo"
              checked={form.active_whatsapp_provider === "wallo"}
              onChange={() => setForm({ ...form, active_whatsapp_provider: "wallo" })}
              className="text-[#0066FF] focus:ring-[#0066FF]"
            />
            <span className="text-sm text-[#0F172A]">Wallo.id</span>
          </label>
        </div>
      </div>

      {/* Pancake.id (Botcake) - Show conditionally or always? Let's show always but highlight if active */}
      <div className={`border rounded-lg bg-white p-6 mb-4 ${form.active_whatsapp_provider === 'botcake' ? 'border-[#0066FF] ring-1 ring-[#0066FF]' : 'border-[#E2E8F0] opacity-70'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#0A2540]" />
            <h2 className="text-base font-medium text-[#0F172A]">Pancake.id (Botcake) WhatsApp</h2>
          </div>
          {form.active_whatsapp_provider === 'botcake' && (
            <span className="bg-[#0066FF] text-white text-[10px] px-2 py-0.5 rounded uppercase font-bold tracking-wider">Aktif</span>
          )}
        </div>
        <div className="space-y-4">
          <Field
            label="Public Token / API Key"
            type="password"
            value={form.pancake_api_key}
            onChange={(e) => setForm({ ...form, pancake_api_key: e.target.value })}
            placeholder={data.pancake_api_key_masked || "Page Settings → API → Public Token"}
            hint={data.pancake_api_key_masked ? `Saat ini: ${data.pancake_api_key_masked}` : null}
            testid="settings-pancake-api-key"
          />
          <Field
            label="Page ID"
            value={form.pancake_phone_number_id}
            onChange={(e) => setForm({ ...form, pancake_phone_number_id: e.target.value })}
            placeholder="ID halaman WhatsApp Botcake"
            testid="settings-pancake-phone-id"
          />
          <Field
            label="Flow ID (opsional)"
            value={form.pancake_flow_id}
            onChange={(e) => setForm({ ...form, pancake_flow_id: e.target.value })}
            placeholder="ID flow Botcake untuk template invoice"
            hint="Gunakan Flow ID jika ingin memakai template WhatsApp Business. Kosongkan untuk pesan teks biasa."
            testid="settings-pancake-flow-id"
          />
        </div>
      </div>

      {/* Wallo.id */}
      <div className={`border rounded-lg bg-white p-6 mb-4 ${form.active_whatsapp_provider === 'wallo' ? 'border-[#0066FF] ring-1 ring-[#0066FF]' : 'border-[#E2E8F0] opacity-70'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#0A2540]" />
            <h2 className="text-base font-medium text-[#0F172A]">Wallo.id WhatsApp Engine</h2>
          </div>
          {form.active_whatsapp_provider === 'wallo' && (
            <span className="bg-[#0066FF] text-white text-[10px] px-2 py-0.5 rounded uppercase font-bold tracking-wider">Aktif</span>
          )}
        </div>
        <div className="space-y-4">
          <Field
            label="API URL"
            value={form.wallo_api_url}
            onChange={(e) => setForm({ ...form, wallo_api_url: e.target.value })}
            placeholder="https://wallo.id/api/v1"
            hint="Base URL untuk endpoint Wallo API Anda"
            testid="settings-wallo-api-url"
          />
          <Field
            label="API Key"
            type="password"
            value={form.wallo_api_key}
            onChange={(e) => setForm({ ...form, wallo_api_key: e.target.value })}
            placeholder={data.wallo_api_key_masked || "Masukkan API Key Wallo Anda"}
            hint={data.wallo_api_key_masked ? `Saat ini: ${data.wallo_api_key_masked}` : null}
            testid="settings-wallo-api-key"
          />
          <Field
            label="Device ID"
            value={form.wallo_device_id}
            onChange={(e) => setForm({ ...form, wallo_device_id: e.target.value })}
            placeholder="UUID perangkat dari dashboard Wallo"
            testid="settings-wallo-device-id"
          />
        </div>
      </div>

      {/* Webhook URL */}
      <div className="border border-[#E2E8F0] rounded-lg bg-white p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="w-4 h-4 text-[#0A2540]" />
          <h2 className="text-base font-medium text-[#0F172A]">Webhook URL untuk Pancake</h2>
        </div>
        <p className="text-xs text-[#64748B] mb-3">
          Daftarkan URL ini di pengaturan webhook Pancake/Botcake untuk menerima update status pengiriman & balasan customer.
        </p>
        <div className="flex items-center gap-2 bg-[#F8F9FA] border border-[#E2E8F0] rounded-md px-3 py-2">
          <code className="flex-1 text-xs font-mono text-[#0F172A] truncate" data-testid="webhook-url">
            {data.webhook_url}
          </code>
          <button
            onClick={copyWebhook}
            data-testid="copy-webhook-button"
            className="shrink-0 text-[#0066FF] hover:text-[#0052CC] text-xs font-medium flex items-center gap-1"
          >
            {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Tersalin" : "Copy"}
          </button>
        </div>
      </div>

      {/* Automation / Background Jobs */}
      <div className="border border-[#E2E8F0] rounded-lg bg-white p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 text-[#0A2540]" />
            <h2 className="text-base font-medium text-[#0F172A]">Otomatisasi Latar Belakang (Background Jobs)</h2>
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between border-b border-[#E2E8F0] pb-6">
            <div className="pr-4">
              <div className="text-sm font-medium text-[#0F172A]">Auto Sync Accurate.id</div>
              <div className="text-xs text-[#64748B] mt-1 leading-relaxed">
                Menarik data pelanggan dan invoice baru dari Accurate secara diam-diam.
              </div>
            </div>
            <Switch
              checked={form.auto_sync_enabled}
              onCheckedChange={(v) => setForm({ ...form, auto_sync_enabled: v })}
            />
          </div>

          {form.auto_sync_enabled && (
            <div className="border-b border-[#E2E8F0] pb-6">
              <Field
                label="Interval Sinkronisasi (Menit)"
                type="number"
                value={form.auto_sync_interval}
                onChange={(e) => setForm({ ...form, auto_sync_interval: parseInt(e.target.value) || 1 })}
                placeholder="15"
                hint="Seberapa sering Midware harus mengecek Accurate.id (Disarankan: 15 menit)."
              />
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="pr-4">
              <div className="text-sm font-medium text-[#0F172A]">Auto Send WA untuk Invoice Baru</div>
              <div className="text-xs text-[#64748B] mt-1 leading-relaxed">
                Jika diaktifkan, saat Auto Sync mendeteksi ada invoice baru dari Accurate, pesan WA "Invoice Baru" akan langsung dikirim detik itu juga ke pelanggan tanpa perlu Anda menekan tombol Kirim manual.
              </div>
            </div>
            <Switch
              checked={form.auto_send_new_invoice}
              onCheckedChange={(v) => setForm({ ...form, auto_send_new_invoice: v })}
            />
          </div>
        </div>
      </div>

      <div className="flex items-start gap-2 text-xs text-[#B45309] bg-[#FEF3C7] rounded-md p-3 mb-6">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          API key disimpan terenkripsi di database. Field kosong tidak menimpa nilai yang sudah ada.
        </div>
      </div>

      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={save}
          disabled={saving}
          data-testid="settings-save-button"
          className="bg-[#0A2540] text-white hover:bg-[#1E3A8A] rounded-md px-5 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Simpan Pengaturan
        </button>
      </div>

      {/* Danger Zone */}
      <div className="border border-red-200 rounded-lg bg-red-50 p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-red-800">Zona Bahaya (Danger Zone)</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="text-xs text-red-600 max-w-md">
            Hapus seluruh data Invoice, Pelanggan, dan Riwayat Pesan dari database Midware. Data di Accurate Anda <strong>TIDAK</strong> akan terpengaruh. Berguna untuk mengulang testing dari awal.
          </div>
          <button
            onClick={async () => {
              if (window.confirm("Apakah Anda yakin ingin menghapus SEMUA data uji coba (Invoice, Pelanggan, Log) di Midware?")) {
                try {
                  const res = await api.post("/admin/clear-data");
                  toast.success(res.data?.message || "Data berhasil dihapus");
                  setTimeout(() => window.location.reload(), 1500);
                } catch (e) {
                  toast.error("Gagal menghapus data");
                }
              }
            }}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap"
          >
            Bersihkan Data
          </button>
        </div>
      </div>
    </div>
  );
}
